import { Component, Input } from '@angular/core';

/**
 * Compact "N monitored" indicator for the Cloud header, shown beside the cloud
 * provider selector. Presentational only: the caller computes `count` for the
 * current provider/account/region scope and passes it in, so the same badge can
 * later surface Azure counts without change.
 *
 * States:
 *   - loading → a muted skeleton pill (no misleading number while data loads)
 *   - ready   → a subtle status pill; the dot is green when >0, grey at 0
 *
 * The caller is responsible for hiding the badge when it would be misleading
 * (e.g. no connection / no accounts).
 */
@Component({
  selector: 'app-monitored-count',
  template: `
    <!-- Loading: lightweight skeleton, sized to match the ready pill -->
    <span
      *ngIf="loading"
      class="inline-flex h-[26px] w-[104px] animate-pulse rounded-full border border-slate-200 bg-slate-100"
      role="status"
      aria-label="Loading monitored services"
    ></span>

    <!-- Ready: subtle pill, visually secondary to the provider selector -->
    <span
      *ngIf="!loading"
      class="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600"
      role="status"
      [attr.aria-label]="safeCount + ' services monitored in the current scope'"
      [attr.title]="safeCount + ' services monitored in the current scope'"
    >
      <span
        class="h-1.5 w-1.5 rounded-full"
        [ngClass]="safeCount > 0 ? 'bg-emerald-500' : 'bg-slate-300'"
        aria-hidden="true"
      ></span>
      <span><span class="font-semibold text-slate-900">{{ safeCount }}</span> monitored</span>
    </span>
  `,
})
export class MonitoredCountBadgeComponent {
  /** Monitored-services count for the current scope. `null` renders as 0. */
  @Input() count: number | null = null;
  /** When true, shows a skeleton placeholder instead of a number. */
  @Input() loading = false;

  get safeCount(): number {
    return this.count ?? 0;
  }
}
